<template>
  <v-menu
    v-model="menuOpen"
    location="bottom start"
    :close-on-content-click="false"
    transition="scale-transition"
    @update:model-value="onMenuToggle"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="outlined"
        rounded="lg"
        class="filter-style-btn"
      >
        <v-icon icon="mdi-account-plus-outline" start />
        Add Job Profile
      </v-btn>
    </template>
    <v-card min-width="300" max-height="380" class="dropdown-card">
      <div class="pa-3 pb-0">
        <v-text-field
          v-model="searchText"
          density="compact"
          variant="outlined"
          flat
          hide-details
          placeholder="Search job profiles…"
          autofocus
          color="brandPrimary"
          base-color="textDarkest"
          @keydown.escape="menuOpen = false"
        />
      </div>
      <v-list class="py-0 dropdown-list" density="compact">
        <template v-if="!searchText.trim()">
          <v-list-item disabled>
            <span class="text-body-2 text-medium-emphasis">Type to search profiles</span>
          </v-list-item>
        </template>
        <template v-else-if="filteredGroups.length === 0">
          <v-list-item disabled>
            <span class="text-body-2 text-medium-emphasis">No matching profiles</span>
          </v-list-item>
        </template>
        <template v-else>
          <template
            v-for="group in filteredGroups"
            :key="group.familyKey"
          >
            <v-list-item class="family-header-item" disabled>
              <span class="text-caption family-label">{{ group.familyName }}</span>
            </v-list-item>
            <v-list-item
              v-for="item in group.profiles"
              :key="item.ulid"
              class="dropdown-list-item"
              @click="handleSelect(item.ulid)"
            >
              <div class="dropdown-profile-row d-flex align-end flex-wrap">
                <span class="text-body-2 item-label">{{ item.title }}</span>
                <span
                  v-if="item.jobCode"
                  class="job-architecture-job-code text-body-3 ml-2"
                >
                  {{ item.jobCode }}
                </span>
              </div>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';

const props = defineProps({
  existingUlids: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add-profile']);

const menuOpen = ref(false);
const searchText = ref('');

const jobArchitectureStore = useJobArchitecture();
const { treeData } = storeToRefs(jobArchitectureStore);
const { loadTree } = jobArchitectureStore;

const existingSet = computed(() => new Set(props.existingUlids || []));

const filteredGroups = computed(() => {
  const query = searchText.value.trim().toLowerCase();

  if (!query) return [];

  const groups = treeData.value?.groups || [];
  const result = [];

  groups.forEach((group, groupIndex) => {
    (group.families || []).forEach((family, familyIndex) => {
      const matching = (family.profiles || []).filter((profile) => {
        const titleMatch = (profile.title || '').toLowerCase().includes(query);
        const codeMatch = (profile.hris_job_code || '')
          .toLowerCase()
          .includes(query);

        return profile.ulid
          && !existingSet.value.has(profile.ulid)
          && (titleMatch || codeMatch);
      });

      if (matching.length) {
        result.push({
          familyKey: family.ulid || `g${groupIndex}-f${familyIndex}`,
          familyName: family.name || 'Uncategorized',
          profiles: matching.map((profile) => ({
            ulid: profile.ulid,
            title: profile.title || profile.ulid,
            jobCode: profile.hris_job_code,
          })),
        });
      }
    });
  });

  return result;
});

const onMenuToggle = async (open) => {
  if (open && !treeData.value) {
    await loadTree();
  }

  if (!open) {
    searchText.value = '';
  }
};

const handleSelect = (ulid) => {
  emit('add-profile', ulid);

  menuOpen.value = false;
  searchText.value = '';
};
</script>

<style scoped lang="less">
.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}

.dropdown-card {
  border: 0 !important;
  box-shadow: 0 10px 50px 0 fade(@grey-900, 10%) !important;
  border-radius: 8px;
}

.dropdown-list {
  max-height: 280px;
  overflow-y: auto;
  border-radius: 0 0 8px 8px;
}

.dropdown-list-item {
  min-height: 40px;
  padding-left: 28px;
  cursor: pointer;
}

.dropdown-profile-row {
  width: 100%;
}

.job-architecture-job-code {
  color: @text-lighter;
  line-height: 135% !important;
}

.family-header-item {
  min-height: 28px;
  padding-top: 4px;
  pointer-events: none;
}

.family-label {
  font-weight: 600;
  color: @text-light;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-label {
  margin-left: 8px;
  color: fade(@grey-700, 95%);
}

.dropdown-list-item:hover .item-label {
  color: @text-brand;
}
</style>
